import type { EffectResource } from '@/features/legends/api/resources/effects'
import type {
  DocumentOptionResource,
  LegendQuestionResource,
  NumericFormatResource,
  SelectOptionResource,
} from '@/features/legends/api/resources/questions'
import {
  mapEffectResourceToEffect,
  mapEffectToResource,
} from '@/features/legends/effects/effect-mappers'
import type { LegacyIdFixer } from '@/features/legends/editor/legacy-ids'
import {
  mapUnknownResourceToElement,
  UnknownElementError,
} from '@/features/legends/editor/legend-document'
import {
  mapValueTypeResourceToValueType,
  mapValueTypeToResource,
} from '@/features/legends/editor/value-type-mappers'
import {
  mapRuleResourcesToRules,
  mapRulesToResources,
} from '@/features/legends/filters/rule-mappers'
import type {
  DocumentOption,
  NumericFormat,
  Question,
  SelectOption,
} from './questions'

/**
 * Maps a legend element, fixing legacy question IDs and the references to
 * them.
 *
 * @param resource
 * @param ids
 */
export function mapQuestionResourceToQuestion(
  resource: LegendQuestionResource,
  ids: LegacyIdFixer,
): Question {
  const base = {
    id: ids.declare(resource.id),
    triggerWhen: resource.triggerWhen
      ? mapRuleResourcesToRules(resource.triggerWhen, ids)
      : undefined,
  }
  const asking = {
    ...base,
    question: resource.question,
    description: resource.description,
  }
  const mapEffect = (effect: EffectResource) =>
    mapEffectResourceToEffect(effect, ids)

  switch (resource.type) {
    case 'BOOL':
      return {
        ...asking,
        type: 'Bool',
        yes: resource.yes && mapEffect(resource.yes),
        no: resource.no && mapEffect(resource.no),
      }
    case 'BREAKER':
      return { ...asking, type: 'Breaker' }
    case 'DATE_TIME':
      return {
        ...asking,
        type: 'DateTime',
        collectTime: resource.collect_time,
        disallowPast: resource.disallow_past,
        disallowFuture: resource.disallow_future,
      }
    case 'DOCUMENTS':
      return {
        ...asking,
        type: 'Documents',
        options: resource.options?.map((option) =>
          mapDocumentOptionResource(option, ids),
        ),
      }
    case 'EFFECT':
      return {
        ...base,
        type: 'Effect',
        label: resource.label,
        effect: mapEffect(resource),
      }
    case 'INVENTORY_ITEM':
      return {
        ...base,
        type: 'InventoryItem',
        label: resource.label,
        inventoryItemId: resource.inventory_item_id,
        quantity: resource.quantity,
      }
    case 'NUMERIC':
      return {
        ...asking,
        type: 'Numeric',
        format: resource.format && mapNumericFormatResource(resource.format),
        effect: resource.effect && mapEffect(resource.effect),
      }
    case 'PATH':
      return {
        ...base,
        type: 'Path',
        label: resource.label,
        questions: resource.questions?.map((question) =>
          mapQuestionResourceToQuestion(question, ids),
        ),
      }
    case 'PERSONAL_ORGANIZER_COLLECTED_VALUE':
      return {
        ...base,
        type: 'PersonalOrganizerCollectedValue',
        label: resource.label,
        collectBindingId: resource.collectBindingId,
        expr: resource.expr,
      }
    case 'PERSONAL_ORGANIZER_PREFILL':
      return {
        ...base,
        type: 'PersonalOrganizerPrefill',
        prefillBindingId: resource.prefillBindingId,
        expr: resource.expr,
      }
    case 'SELECT':
      return {
        ...asking,
        type: 'Select',
        multi: resource.multi,
        options: resource.options?.map((option) =>
          mapSelectOptionResource(option, ids),
        ),
        effect: resource.effect && mapEffect(resource.effect),
      }
    case 'TEXT':
      return { ...asking, type: 'Text' }
    case 'VARIABLE':
      return {
        ...base,
        type: 'Variable',
        label: resource.label,
        valueType:
          resource.valueType &&
          mapValueTypeResourceToValueType(resource.valueType),
        expr: resource.expr,
      }
  }
  return mapUnknownResourceToElement(resource)
}

/**
 * Maps a legend element to the API's, with the fields its type has.
 *
 * @param question
 */
export function mapQuestionToResource(
  question: Question,
): LegendQuestionResource {
  if (question.type === 'Unknown') {
    throw new UnknownElementError('question', question)
  }
  const base = {
    id: question.id,
    triggerWhen:
      question.triggerWhen && mapRulesToResources(question.triggerWhen),
  }

  switch (question.type) {
    case 'Bool':
      return {
        ...base,
        type: 'BOOL',
        question: question.question,
        description: question.description,
        yes: question.yes && mapEffectToResource(question.yes),
        no: question.no && mapEffectToResource(question.no),
      }
    case 'Breaker':
      return {
        ...base,
        type: 'BREAKER',
        question: question.question,
        description: question.description,
      }
    case 'DateTime':
      return {
        ...base,
        type: 'DATE_TIME',
        question: question.question,
        description: question.description,
        collect_time: question.collectTime,
        disallow_past: question.disallowPast,
        disallow_future: question.disallowFuture,
      }
    case 'Documents':
      return {
        ...base,
        type: 'DOCUMENTS',
        question: question.question,
        description: question.description,
        options: question.options?.map((option): DocumentOptionResource => ({
          id: option.id,
          text: option.text,
          triggerWhen:
            option.triggerWhen && mapRulesToResources(option.triggerWhen),
          document_tags: option.documentTagIds,
        })),
      }
    case 'Effect':
      return {
        ...base,
        type: 'EFFECT',
        label: question.label,
        mutators: mapEffectToResource(question.effect).mutators,
      }
    case 'InventoryItem':
      return {
        ...base,
        type: 'INVENTORY_ITEM',
        label: question.label,
        inventory_item_id: question.inventoryItemId,
        quantity: question.quantity,
      }
    case 'Numeric':
      return {
        ...base,
        type: 'NUMERIC',
        question: question.question,
        description: question.description,
        format: question.format && mapNumericFormatToResource(question.format),
        effect: question.effect && mapEffectToResource(question.effect),
      }
    case 'Path':
      return {
        ...base,
        type: 'PATH',
        questions: question.questions?.map(mapQuestionToResource),
        label: question.label,
      }
    case 'PersonalOrganizerCollectedValue':
      return {
        ...base,
        type: 'PERSONAL_ORGANIZER_COLLECTED_VALUE',
        label: question.label,
        collectBindingId: question.collectBindingId,
        expr: question.expr,
      }
    case 'PersonalOrganizerPrefill':
      return {
        ...base,
        type: 'PERSONAL_ORGANIZER_PREFILL',
        prefillBindingId: question.prefillBindingId,
        expr: question.expr,
      }
    case 'Select':
      return {
        ...base,
        type: 'SELECT',
        question: question.question,
        description: question.description,
        options: question.options?.map((option): SelectOptionResource => ({
          id: option.id,
          text: option.text,
          triggerWhen:
            option.triggerWhen && mapRulesToResources(option.triggerWhen),
          mutators: mapEffectToResource(option.effect).mutators,
        })),
        multi: question.multi,
        effect: question.effect && mapEffectToResource(question.effect),
      }
    case 'Text':
      return {
        ...base,
        type: 'TEXT',
        question: question.question,
        description: question.description,
      }
    case 'Variable':
      return {
        ...base,
        type: 'VARIABLE',
        valueType:
          question.valueType && mapValueTypeToResource(question.valueType),
        label: question.label,
        expr: question.expr,
      }
  }
}

function mapSelectOptionResource(
  resource: SelectOptionResource,
  ids: LegacyIdFixer,
): SelectOption {
  return {
    id: resource.id,
    text: resource.text,
    triggerWhen: resource.triggerWhen
      ? mapRuleResourcesToRules(resource.triggerWhen, ids)
      : undefined,
    effect: mapEffectResourceToEffect(resource, ids),
  }
}

function mapDocumentOptionResource(
  resource: DocumentOptionResource,
  ids: LegacyIdFixer,
): DocumentOption {
  return {
    id: resource.id,
    text: resource.text,
    triggerWhen: resource.triggerWhen
      ? mapRuleResourcesToRules(resource.triggerWhen, ids)
      : undefined,
    documentTagIds: resource.document_tags,
  }
}

function mapNumericFormatResource(
  resource: NumericFormatResource,
): NumericFormat {
  switch (resource.type) {
    case 'Integer':
      return { type: 'Integer' }
    case 'Decimal':
      return { type: 'Decimal', places: resource.places }
  }
  return mapUnknownResourceToElement(resource)
}

function mapNumericFormatToResource(
  format: NumericFormat,
): NumericFormatResource {
  switch (format.type) {
    case 'Integer':
      return { type: 'Integer' }
    case 'Decimal':
      return { type: 'Decimal', places: format.places }
    case 'Unknown':
      throw new UnknownElementError('number format', format)
  }
}
